import _ from "lodash";
import React from "react";
import classes from "./PostSeenLikeModal.scss";
import UIModal from "UIComponents/UIModal";
import I18nHOC from "UIComponents/I18nHOC";
import { IconButton, Avatar, Tooltip } from "@toddle-edu/ds-web";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import StudentAlerts from "AppComponents/StudentAlerts";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { AVATAR_COLORS } from "Constants/colorConstants";
import {
  getPostLikesDetailsQuery,
  getPostSeenDetailsQuery,
  getActivityPostLikesDetailsQuery,
  getRoutinePostLikesDetailsQuery,
} from "AppComponents/Post/modules/PostQuery";
import {
  getDefaultStudentNameOrderPreference,
  getDefaultStudentIdentifierNamePreference,
} from "modules/CommonSelectors";
import { hashString } from "Utils";
import { getStudentIdentifierName } from "Utils/GetFormattedStudentName";

import { getFormattedUserName } from "Utils/GetFormattedUserName";

const UserItem = props => {
  const { user, subText } = props;
  const userName = getFormattedUserName({ user });

  const alertTypes = _.get(user, "alertTypes", []);

  return (
    <div className={classes.userContainer}>
      <div>
        <Avatar
          size={"small"}
          iconType={"individual"}
          color={AVATAR_COLORS[hashString(user?.id) % AVATAR_COLORS.length]}
          src={user?.profileImage}
          shade={"light"}
        />
      </div>
      <div
        className={classes.userDetailsContainer}
        data-test-id="journal-feed-postcard-postseenusercontainer"
      >
        <div className={classes.userNameAndAlertsContainer}>
          <div
            className={classes.usernameText}
            data-test-id="journal-feed-postcard-postseenusername"
          >
            {userName}
          </div>
          <StudentAlerts studentId={user.id} alertTypes={alertTypes} />
        </div>
        <Tooltip tooltip={subText} placement="bottom">
          <div
            className={classes.userSubText}
            data-test-id="journal-feed-postcard-postseenusersubtext"
          >
            {subText}
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

const PostSeenLikeModal = props => {
  const {
    isModalOpen,
    closeSeenLikeModal,
    likes,
    views,
    likesCount,
    viewsCount,
    t,
    showLikes,
    showViews,
    defaultStudentNameOrderPreference,
    defaultStudentIdentifierNamePreference,
  } = props;

  let userList = [];
  if (showLikes) userList = _.map(likes, like => _.get(like, "user", {}));
  else if (showViews) userList = _.map(views, view => _.get(view, "user", {}));

  const getUserSubText = user => {
    const { students, t } = props;
    if (user.type == "staff") {
      return t("common:teacher");
    } else if (user.type == "parent") {
      const children = _.get(user, "children", []);
      const intersection = _.intersectionBy(children, students, "id") || [];
      let childrenString = "";
      if (intersection.length == 1) {
        childrenString = t("common:apostrophe", {
          name: getStudentIdentifierName({
            student: _.get(intersection, "[0]", {}),
            defaultStudentIdentifierNamePreference,
          }),
        });
      } else if (intersection.length == 2) {
        childrenString = t("journal:label_and_label_apostrophe", {
          nameOne: getStudentIdentifierName({
            student: _.get(intersection, "[0]", {}),
            defaultStudentIdentifierNamePreference,
          }),
          nameTwo: getStudentIdentifierName({
            student: _.get(intersection, "[1]", {}),
            defaultStudentIdentifierNamePreference,
          }),
        });
      } else if (intersection.length == 0) {
        childrenString = "";
      } else {
        const more = intersection.length - 2;
        childrenString = t("journal:label_count_more_student", {
          name: getStudentIdentifierName({
            student: _.get(intersection, "[0]", {}),
            defaultStudentIdentifierNamePreference,
          }),
          count: intersection.length - 2,
        });
      }

      return `${childrenString}Family`;
    } else if (user.type == "student") {
      return t("common:student");
    }
  };

  return (
    <UIModal
      isOpen={isModalOpen}
      modalContent={classes.modalContainer}
      overlayContainer={classes.modalOverlayContainer}
    >
      <div className={classes.modalBodyContainer}>
        <div className={classes.modalHeaderContainer}>
          <div className={classes.likeSeenTitle}>
            {showViews && !showLikes && t("journal:family_views")}
            {showLikes && !showViews && t("journal:likes")}
          </div>
          <IconButton
            icon={<CloseOutlined />}
            onClick={closeSeenLikeModal}
            variant="plain"
            testId="journal-feed-postcard-seenlikeclose"
          />
        </div>
        {(likesCount > 0 || viewsCount > 0) && (
          <div className={classes.taggedListContainer}>
            {_.map(userList, user => (
              <UserItem
                key={user.id}
                user={user}
                subText={getUserSubText(user)}
                defaultStudentNameOrderPreference={
                  defaultStudentNameOrderPreference
                }
                defaultStudentIdentifierNamePreference={
                  defaultStudentIdentifierNamePreference
                }
              />
            ))}
          </div>
        )}
      </div>
    </UIModal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  const defaultStudentIdentifierNamePreference = getDefaultStudentIdentifierNamePreference(
    state
  );
  return {
    isData: true,
    isLoading: false,
    defaultStudentNameOrderPreference,
    defaultStudentIdentifierNamePreference,
  };
};

export default compose(
  I18nHOC({ resource: ["journal"] }),
  connect(mapStateToProps, null),
  // Fetch users who liked the post
  graphql(getPostLikesDetailsQuery, {
    name: "getPostLikesDetails",
    skip: ({ showLikes, postType }) =>
      _.isUndefined(showLikes) ||
      !showLikes ||
      postType === "STUDENT_ACTIVITY" ||
      postType === "STUDENT_TEMPLATE_RESPONSE",
    options: ({ postId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: postId },
    }),
    props: ({ getPostLikesDetails, ownProps: { isData, isLoading } }) => {
      const post = _.get(getPostLikesDetails, "node", {});
      const likes = _.map(_.get(post, "likes.edges", []), edge => edge.node);
      const likesCount = _.get(post, "likes.totalCount", 0);
      const students = _.map(
        _.get(post, "students.edges", []),
        edge => edge.node
      );

      return {
        getPostLikesDetails,
        post,
        likes,
        likesCount,
        students,
        isData: !_.isEmpty(post) && isData,
        isLoading:
          getPostLikesDetails["networkStatus"] == 1 ||
          getPostLikesDetails["networkStatus"] == 2 ||
          getPostLikesDetails["networkStatus"] == 4 ||
          isLoading,
      };
    },
  }),
  // Fetch users who have seen the post
  graphql(getPostSeenDetailsQuery, {
    name: "getPostSeenDetails",
    skip: ({ showViews }) => _.isUndefined(showViews) || !showViews,
    options: ({ postId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: postId },
    }),
    props: ({ getPostSeenDetails, ownProps: { isData, isLoading } }) => {
      const post = _.get(getPostSeenDetails, "node", {});
      const views = _.map(_.get(post, "seen.edges", []), edge => edge.node);
      const viewsCount = _.get(post, "seen.totalCount", 0);
      const students = _.map(
        _.get(post, "students.edges", []),
        edge => edge.node
      );

      return {
        getPostSeenDetails,
        post,
        views,
        viewsCount,
        students,
        isData: !_.isEmpty(post) && isData,
        isLoading:
          getPostSeenDetails["networkStatus"] == 1 ||
          getPostSeenDetails["networkStatus"] == 2 ||
          getPostSeenDetails["networkStatus"] == 4 ||
          isLoading,
      };
    },
  }),
  graphql(getActivityPostLikesDetailsQuery, {
    name: "getActivityPostLikesDetails",
    skip: ({ showLikes, postType, activityId }) =>
      _.isUndefined(showLikes) ||
      !showLikes ||
      postType !== "STUDENT_ACTIVITY" ||
      _.isEmpty(activityId),
    options: ({ activityId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: activityId },
    }),
    props: ({
      getActivityPostLikesDetails,
      ownProps: { isData, isLoading },
    }) => {
      const post = _.get(getActivityPostLikesDetails, "node", {});
      const likes = _.map(_.get(post, "likes.edges", []), edge => edge.node);
      const likesCount = _.get(post, "likes.totalCount", 0);
      const students = [_.get(post, "student", {})];

      return {
        getPostLikesDetails: getActivityPostLikesDetails,
        post,
        likes,
        likesCount,
        students,
        isData: !_.isEmpty(post) && isData,
        isLoading:
          getActivityPostLikesDetails["networkStatus"] == 1 ||
          getActivityPostLikesDetails["networkStatus"] == 2 ||
          getActivityPostLikesDetails["networkStatus"] == 4 ||
          isLoading,
      };
    },
  }),
  graphql(getRoutinePostLikesDetailsQuery, {
    name: "getActivityPostLikesDetails",
    skip: ({ showLikes, postType, activityId }) =>
      _.isUndefined(showLikes) ||
      !showLikes ||
      postType !== "STUDENT_TEMPLATE_RESPONSE" ||
      _.isEmpty(activityId),
    options: ({ activityId, postType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: activityId,
      },
    }),
    props: ({
      getActivityPostLikesDetails,
      ownProps: { isData, isLoading },
    }) => {
      const post = _.get(getActivityPostLikesDetails, "node", {});
      const likes = _.map(_.get(post, "likes.edges", []), edge => edge.node);
      const likesCount = _.get(post, "likes.totalCount", 0);
      const students = [_.get(post, "student", {})];

      return {
        getPostLikesDetails: getActivityPostLikesDetails,
        post,
        likes,
        likesCount,
        students,
        isData: !_.isEmpty(post) && isData,
        isLoading:
          getActivityPostLikesDetails["networkStatus"] == 1 ||
          getActivityPostLikesDetails["networkStatus"] == 2 ||
          getActivityPostLikesDetails["networkStatus"] == 4 ||
          isLoading,
      };
    },
  })
)(PostSeenLikeModal);
